/**
 * Data-only Google Places autocomplete.
 *
 * WHY THIS EXISTS
 * ---------------
 * The booking form used `new google.maps.places.Autocomplete(input)`, which
 * renders its own dropdown in its own DOM. We cannot put anything into that
 * list, so a customer's own frequently-used addresses could only ever be shown
 * in a *second*, competing dropdown — and the moment they typed a letter,
 * Google's list took over and their depot disappeared.
 *
 * These functions return predictions as plain data instead, so the app can
 * render one list containing both.
 *
 * TWO BACKENDS, ON PURPOSE
 * ------------------------
 * `AutocompleteSuggestion` is Google's current API and the one this prefers.
 * It is only present when Places API (New) is enabled for the key, and the app
 * currently loads the SDK with the legacy `libraries: 'places'`, so it may not
 * be. `AutocompleteService` is the deprecated-but-present data-only API and is
 * the fallback.
 *
 * Both are *data* APIs — neither renders UI — so the dropdown does not care
 * which one answered. That is the point: sprint-6.13 (migrating off the
 * deprecated widget) becomes a change to `predict`/`resolve` here rather than
 * a rewrite of the booking form.
 *
 * SESSION TOKENS
 * --------------
 * Places bills autocomplete per *session*: many keystrokes plus one details
 * fetch. The token is what groups them. Getting this wrong means being billed
 * per request instead of per session, so a session is created on first use and
 * deliberately discarded after each `resolve` — see `dispose`.
 */

export interface Prediction {
  place_id: string
  /** Main line — usually the establishment or street. */
  primary: string
  /** Supporting line — usually the locality. */
  secondary: string
}

export interface ResolvedPlace {
  /** The combined string the backend stores. See combinePlaceName. */
  name: string
  formatted_address: string
  lat: number
  lng: number
  place_id: string
}

/**
 * The canonical address string: `"Place Name; Formatted Address"`.
 *
 * This is load-bearing and must not be "tidied". It is what
 * `customer_order.locations[].name` holds, what order history, tracking and
 * the driver app display, and what `JSON_EXTRACT(locations,'$[0].name')`
 * returns for every list query. Lifted verbatim out of NewRequest's setPlace
 * so the suggestion path and the Google path produce byte-identical values —
 * otherwise the same physical address would split into two different
 * suggestions depending on how it was entered.
 */
export function combinePlaceName(placeName: string, formattedAddress: string): string {
  const n = (placeName || '').trim()
  const f = (formattedAddress || '').trim()
  if (n && f && n !== f) return `${n}; ${f}`
  if (f && !n) return f
  return n
}

/** Split a stored combined name back into two lines for display only. */
export function splitPlaceName(name: string): { primary: string; secondary: string } {
  const raw = String(name || '')
  const i = raw.indexOf('; ')
  if (i < 0) return { primary: raw, secondary: '' }
  return { primary: raw.slice(0, i), secondary: raw.slice(i + 2) }
}

const mapsApi = (): any => (globalThis as any)?.google?.maps

/** Kenya-only, matching the options the legacy widget was configured with. */
const REGION = 'ke'

/**
 * Whether Places API (New) is actually USABLE, not merely present.
 *
 * This distinction cost a regression. `AutocompleteSuggestion` ships with the
 * Maps SDK whether or not the project has Places API (New) enabled, so a
 * presence check passes and then every call fails:
 *
 *   403 places.googleapis.com/$rpc/…/AutocompletePlaces
 *   RpcError: Requests to this API … AutocompletePlaces are blocked.
 *
 * The old widget used the legacy API and worked, so searches that succeeded in
 * production returned nothing here — silently, because the failure was caught
 * and turned into an empty list.
 *
 * Module-scoped and sticky: the key either has the API or it does not, so one
 * failure downgrades every input on the page for the rest of the session
 * rather than costing a 403 per field per keystroke. It self-heals on the next
 * page load if the API is later enabled.
 */
let newApiUsable = true

export interface PlacesSession {
  predict(input: string): Promise<Prediction[]>
  resolve(placeId: string): Promise<ResolvedPlace | null>
  dispose(): void
}

export function isPlacesReady(): boolean {
  const m = mapsApi()
  return !!(m?.places?.AutocompleteSuggestion || m?.places?.AutocompleteService)
}

export function createPlacesSession(): PlacesSession {
  let token: any = null
  let legacyService: any = null
  let placesService: any = null

  const newApi = () => mapsApi()?.places?.AutocompleteSuggestion

  const ensureToken = () => {
    const m = mapsApi()
    if (!token && m?.places?.AutocompleteSessionToken) {
      token = new m.places.AutocompleteSessionToken()
    }
    return token
  }

  // ── Preferred: Places API (New), data-only ────────────────────────────
  async function predictNew(input: string): Promise<Prediction[]> {
    const { suggestions } = await newApi().fetchAutocompleteSuggestions({
      input,
      sessionToken: ensureToken(),
      includedRegionCodes: [REGION],
    })
    return (suggestions || [])
      .map((s: any) => s.placePrediction)
      .filter(Boolean)
      .map((p: any) => ({
        place_id: p.placeId,
        primary: p.mainText?.text ?? p.text?.text ?? '',
        secondary: p.secondaryText?.text ?? '',
      }))
  }

  async function resolveNew(placeId: string): Promise<ResolvedPlace | null> {
    const m = mapsApi()
    const place = new m.places.Place({ id: placeId })
    await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'location'] })
    const loc = place.location
    if (!loc) return null
    const formatted = place.formattedAddress || ''
    return {
      name: combinePlaceName(place.displayName || '', formatted),
      formatted_address: formatted,
      lat: typeof loc.lat === 'function' ? loc.lat() : loc.lat,
      lng: typeof loc.lng === 'function' ? loc.lng() : loc.lng,
      place_id: placeId,
    }
  }

  // ── Fallback: the deprecated data-only API ────────────────────────────
  function predictLegacy(input: string): Promise<Prediction[]> {
    const m = mapsApi()
    if (!legacyService) legacyService = new m.places.AutocompleteService()
    return new Promise((resolve) => {
      legacyService.getPlacePredictions(
        {
          input,
          sessionToken: ensureToken(),
          componentRestrictions: { country: REGION },
        },
        (res: any[], status: string) => {
          // ZERO_RESULTS is a normal answer, not a failure — resolve empty
          // rather than rejecting, so the dropdown just shows the
          // frequently-used entries.
          if (status !== m.places.PlacesServiceStatus.OK || !res) return resolve([])
          resolve(
            res.map((p) => ({
              place_id: p.place_id,
              primary: p.structured_formatting?.main_text ?? p.description ?? '',
              secondary: p.structured_formatting?.secondary_text ?? '',
            })),
          )
        },
      )
    })
  }

  function resolveLegacy(placeId: string): Promise<ResolvedPlace | null> {
    const m = mapsApi()
    if (!placesService) {
      // PlacesService needs a node to attach to; it never renders into it.
      placesService = new m.places.PlacesService(document.createElement('div'))
    }
    return new Promise((resolve) => {
      placesService.getDetails(
        {
          placeId,
          fields: ['name', 'formatted_address', 'geometry', 'place_id'],
          sessionToken: ensureToken(),
        },
        (place: any, status: string) => {
          if (status !== m.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
            return resolve(null)
          }
          const formatted = place.formatted_address || ''
          resolve({
            name: combinePlaceName(place.name || '', formatted),
            formatted_address: formatted,
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            place_id: place.place_id || placeId,
          })
        },
      )
    })
  }

  return {
    async predict(input: string) {
      const q = (input || '').trim()
      if (q.length < 2 || !isPlacesReady()) return []

      if (newApiUsable && newApi()) {
        try {
          return await predictNew(q)
        } catch (err) {
          // Downgrade and retry the SAME query on the legacy API, so the
          // keystroke that discovered the problem still returns results
          // instead of showing an empty list.
          newApiUsable = false
          console.warn('[placesAutocomplete] Places API (New) unavailable — '
            + 'falling back to the legacy API for this session', err)
        }
      }

      try {
        return await predictLegacy(q)
      } catch (err) {
        // A prediction failure must never break typing — the customer can
        // still pick a frequently-used address or keep typing.
        console.warn('[placesAutocomplete] prediction failed', err)
        return []
      }
    },

    async resolve(placeId: string) {
      if (!placeId || !isPlacesReady()) return null

      if (newApiUsable && newApi()) {
        try {
          const out = await resolveNew(placeId)
          token = null
          return out
        } catch (err) {
          newApiUsable = false
          console.warn('[placesAutocomplete] Places API (New) unavailable on '
            + 'resolve — falling back to the legacy API', err)
        }
      }

      try {
        const out = await resolveLegacy(placeId)
        // The session ends at the details fetch. Dropping the token here is
        // what keeps the next lookup a new billable session rather than an
        // ever-growing one.
        token = null
        return out
      } catch (err) {
        console.warn('[placesAutocomplete] resolve failed', err)
        token = null
        return null
      }
    },

    dispose() {
      token = null
      legacyService = null
      placesService = null
    },
  }
}
